import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useBillingPortal } from '@/hooks/use-billing-portal'
import { apiV2 } from '@/lib/api-v2-client'
import { billingProvider } from '@/lib/billing-provider'
import { canManageBilling } from '@/lib/subscription-access'

const failedOn = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null

/**
 * Shown on every dashboard page while the subscription is `past_due` (OpenSpec 138).
 * Access stays on while Paddle retries the payment; this tells the customer to fix it.
 * Paddle: owner or admin gets the portal button, a member is told to ask. App Store:
 * the payment method lives in iPhone Settings, so there is no button.
 */
export function PastDueBanner() {
  const { user } = useAuth()
  const portal = useBillingPortal()
  const { data } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => apiV2.getSubscription(),
    staleTime: 5 * 60_000,
  })
  const subscription = data?.data
  if (subscription?.status !== 'past_due') return null

  const date = failedOn(subscription.past_due_since)
  const apple = billingProvider(subscription) === 'apple'
  const canFix = !apple && canManageBilling(user)

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-status-degraded bg-status-degraded-soft px-4 py-2 text-sm"
    >
      <div className="flex items-start gap-2 text-status-degraded">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p>
          <span className="font-semibold">Your last payment failed{date ? ` on ${date}` : ''}.</span>{' '}
          {apple
            ? 'Open Settings on your iPhone, tap your name, then Subscriptions, and update your payment method.'
            : canFix
              ? 'Update your payment method to keep your plan.'
              : 'Ask your owner or admin to update the payment method.'}
        </p>
      </div>
      {canFix && (
        <Button
          variant="outline"
          size="sm"
          className="border-status-degraded bg-transparent text-status-degraded hover:bg-status-degraded-soft"
          disabled={portal.loading}
          onClick={portal.open}
        >
          {portal.loading ? 'Opening...' : 'Update payment method'}
        </Button>
      )}
    </div>
  )
}
